<template>
  <PageLayout :title="$t('profile.title')">
    <ContentCard v-if="!isLoading">
      <form class="profile-form" @submit.prevent="handleUpdate">
        <div class="form-group">
          <label>{{ $t('global.email') }}</label>
          <input v-model.trim="form.email" type="email" class="form-control" required>
        </div>
        <div class="form-group">
          <label>{{ $t('global.name') }}</label>
          <input v-model.trim="form.name" type="text" class="form-control" required>
        </div>
        <div class="form-group">
          <label>{{ $t('global.phone') }}</label>
          <input v-model.trim="form.phone" type="text" class="form-control">
        </div>
        <div class="form-group">
          <label>{{ $t('global.timezone') }}</label>
          <select v-model.number="form.timezone" class="form-control">
            <option v-for="tz in timezones" :key="tz.value" :value="tz.value">{{ tz.label }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ $t('profile.api_key') }}</label>
          <input :value="form.api_key" type="text" class="form-control" readonly>
        </div>
        <div class="form-group">
          <label>{{ $t('profile.new_password') }} <span class="text-muted">({{ $t('profile.new_password_hint') }})</span></label>
          <input v-model="form.password" type="password" class="form-control" autocomplete="new-password">
        </div>
        <div class="profile-form__actions">
          <button type="submit" class="btn btn-primary btn-block" :disabled="isSaving">{{ $t('common.save') }}</button>
          <button type="button" class="btn btn-outline-primary btn-block" :disabled="isSaving" @click="handleResetApiKey">
            {{ $t('profile.regenerate_key') }}
          </button>
        </div>
      </form>
    </ContentCard>
    <ContentCard v-else>
      <div class="profile-loading">
        <div class="spinner-border text-primary" role="status"></div>
      </div>
    </ContentCard>
  </PageLayout>
</template>

<script>
import { mapActions } from 'vuex'

const DEFAULT_TIMEZONES = [
  { value: 7, label: 'GMT +7' },
  { value: 8, label: 'GMT +8' },
  { value: 0, label: 'GMT 0' },
]

export default {
  data() {
    return {
      isLoading: true,
      isSaving: false,
      form: {
        name: '',
        email: '',
        phone: '',
        timezone: 7,
        api_key: '',
        password: '',
      },
      timezones: DEFAULT_TIMEZONES,
    }
  },
  async mounted() {
    await this.loadProfile()
  },
  methods: {
    ...mapActions('user', ['fetchProfile', 'updateProfile', 'resetApiKey']),
    async loadProfile() {
      this.isLoading = true
      try {
        const user = await this.fetchProfile()
        this.form = { ...user, password: '' }
      } catch (error) {
        this.$toast.error(error.response?.data?.message || this.$t('profile.load_failed'))
        this.$router.push('/dashboard')
      } finally {
        this.isLoading = false
      }
    },
    async handleUpdate() {
      this.isSaving = true
      try {
        const payload = {
          name: this.form.name,
          email: this.form.email,
          phone: this.form.phone,
          timezone: this.form.timezone,
        }
        if (this.form.password) payload.password = this.form.password

        const response = await this.updateProfile(payload)
        this.$toast.success(response.message)
        await this.loadProfile()
      } catch (error) {
        this.$toast.error(error.response?.data?.message || this.$t('profile.update_failed'))
      } finally {
        this.isSaving = false
      }
    },
    async handleResetApiKey() {
      this.isSaving = true
      try {
        const response = await this.resetApiKey()
        this.$toast.success(response.message)
        await this.loadProfile()
      } catch (error) {
        this.$toast.error(error.response?.data?.message || this.$t('profile.regenerate_failed'))
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>

<style scoped>
.profile-form .form-group { margin-bottom: 1.25rem; }
.profile-form label { display: block; margin-bottom: 0.5rem; font-weight: 500; }
.profile-form__actions { display: flex; flex-direction: column; gap: 0.75rem; margin-top: 0.5rem; }
.profile-loading { display: flex; justify-content: center; padding: 3rem 0; }
</style>
